import { compareText } from '$lib/i18n/i18n.svelte';
import type { BuiltInDepartment } from './departments';
import type { BuiltInPlace } from './places';

/** The built-in store departments' fixed colours. Names live in the message catalogue (`departments`). */
export const DEPARTMENT_COLORS: Readonly<Record<BuiltInDepartment, string>> = {
	gron: '#7ED957',
	kod: '#FF8A7A',
	mejeri: '#8EC5FF',
	brod: '#F7C873',
	kolonial: '#C9A7FF',
	frost: '#7FE3E0',
	andet: '#E4DDD0'
};

/** The built-in storage places' fixed colours. Names live in the message catalogue (`places`). */
export const PLACE_COLORS: Readonly<Record<BuiltInPlace, string>> = {
	fryser: '#7FE3E0',
	koel: '#8EC5FF',
	skab: '#F7C873',
	viktualierum: '#C9A7FF'
};

/** A colour mixed into the surface at `percent` %, for tinted boxes and pills. */
export function tint(color: string, percent: number): string {
	return `color-mix(in srgb, ${color} ${percent}%, var(--surface))`;
}

/** Colours a household's own places get in turn, as they are added. */
export const CUSTOM_PLACE_COLORS = ['#FF8FB8', '#7ED957', '#FFA98A', '#FFD27A', '#B9A2FF'] as const;

/** The colours a household's own departments choose from; none of them is a built-in department's. */
export const CUSTOM_DEPARTMENT_COLORS = [
	'#FF8FB8',
	'#7EDCCF',
	'#FFA98A',
	'#FFD27A',
	'#B9A2FF',
	'#FFC53D',
	'#FF6F91',
	'#5ECFA8',
	'#9DB4FF',
	'#E7A6F0',
	'#C9B08F',
	'#B5E36A'
] as const;

/*
 * Units, tags and meal slots below are stored as text in the database (user data), so they stay
 * Danish values rather than catalogue messages. The units themselves come from the server (`state/units.svelte`).
 */

/** The unit of a whole dish in the pantry (portions); set by the app, never picked. */
export const PORTION_UNIT = 'port.';

/** The unit the app counts an item in when nothing else is said. */
export const PIECE_UNIT = 'stk';

export const TAGS = [
	'Vegetar',
	'Hurtig',
	'Børnevenlig',
	'Fisk',
	'Kylling',
	'Pasta',
	'Suppe',
	'Morgenmad'
] as const;

const TAG_COLORS = [
	'#FFC53D',
	'#7ED957',
	'#8EC5FF',
	'#FF8FB8',
	'#C9A7FF',
	'#7FE3E0',
	'#FFA98A',
	'#F7C873'
];

/** The meals every household plans; they can be reordered but not removed (households add their own). */
export const STANDARD_MEAL_SLOTS: readonly string[] = ['Morgen', 'Frokost', 'Middag', 'Aften'];

export const DARK_INK = '#1B1A17';

export const STATUS_COLORS = { danger: '#FF5A36', warn: '#FFC53D', ok: '#7ED957' } as const;

/** Fixed tags keep their colour by position; the household's own tags get a stable one from their name. */
export function tagColor(tag: string): string {
	const index = (TAGS as readonly string[]).indexOf(tag);
	return TAG_COLORS[(index >= 0 ? index : hash(tag)) % TAG_COLORS.length];
}

function hash(text: string): number {
	let value = 0;
	for (const char of text) value = (value * 31 + char.charCodeAt(0)) >>> 0;
	return value;
}

const tagKey = (tag: string) => tag.trim().toLocaleLowerCase('da');

/**
 * The tags to choose and filter by: the fixed tags first, then every other tag the recipes use, sorted. Tags that
 * differ only in case count once (the first spelling wins).
 */
export function recipeTags(recipes: readonly { tags: readonly string[] }[]): string[] {
	const seen = new Set(TAGS.map(tagKey));
	const own: string[] = [];
	for (const tag of recipes.flatMap((recipe) => recipe.tags)) {
		if (seen.has(tagKey(tag))) continue;
		seen.add(tagKey(tag));
		own.push(tag);
	}
	return [...TAGS, ...own.sort(compareText)];
}

/**
 * The chosen tags with a typed one added, in the spelling it already has ("grill" → "Grill"); unchanged when the
 * text is empty or already chosen.
 */
export function withTag(
	chosen: readonly string[],
	text: string,
	known: readonly string[]
): string[] {
	const typed = text.trim();
	const tag = known.find((other) => tagKey(other) === tagKey(typed)) ?? typed;
	return tag && !chosen.includes(tag) ? [...chosen, tag] : [...chosen];
}

/** The known tags not chosen yet that contain the typed text. */
export function suggestTags(
	query: string,
	known: readonly string[],
	chosen: readonly string[]
): string[] {
	const key = tagKey(query);
	return key ? known.filter((tag) => !chosen.includes(tag) && tagKey(tag).includes(key)) : [];
}

/** Recipes get a stable tile colour from their position in the collection. */
export function tileColor(index: number): string {
	return `var(--tile-${index % 6})`;
}
